import type { GameSelection } from '@urbanrats/contracts';

/**
 * Client-only bet draft (the DRAFT state). Amounts are atomic decimal strings; arithmetic in BigInt.
 * Nothing here moves money: only a confirmed command does.
 */
export interface DraftState {
  chipAtomic: string;
  lines: Partial<Record<GameSelection, string>>;
  /** Previous `lines` snapshots for undo. */
  past: Partial<Record<GameSelection, string>>[];
}

export type DraftAction =
  | { type: 'chip'; atomic: string }
  | { type: 'add'; selection: GameSelection }
  /** ×2: every draft line doubled, never above `maxAtomic` per line. */
  | { type: 'double'; maxAtomic: string }
  | { type: 'undo' }
  | { type: 'clear' }
  | { type: 'remove'; selection: GameSelection }
  /** Server accepted this line: drop it from the draft and forget undo history (accepted bets are final). */
  | { type: 'accepted'; selection: GameSelection }
  | { type: 'repeat'; lines: Partial<Record<GameSelection, string>> }
  /** Replace the draft with exactly these lines (e.g. a freebet at its fixed face value). */
  | { type: 'set'; lines: Partial<Record<GameSelection, string>> };

export const initialDraft = (chipAtomic: string): DraftState => ({ chipAtomic, lines: {}, past: [] });

export function draftReducer(s: DraftState, a: DraftAction): DraftState {
  switch (a.type) {
    case 'chip':
      return { ...s, chipAtomic: a.atomic };
    case 'add': {
      const cur = BigInt(s.lines[a.selection] ?? '0');
      return { ...s, past: [...s.past, s.lines], lines: { ...s.lines, [a.selection]: (cur + BigInt(s.chipAtomic)).toString() } };
    }
    case 'double': {
      const max = BigInt(a.maxAtomic);
      const next: DraftState['lines'] = {};
      for (const [k, v] of Object.entries(s.lines) as [GameSelection, string][]) {
        const d = BigInt(v) * 2n;
        next[k] = (d > max ? max : d).toString();
      }
      return Object.keys(next).length === 0 ? s : { ...s, past: [...s.past, s.lines], lines: next };
    }
    case 'undo': {
      if (s.past.length === 0) return s;
      return { ...s, lines: s.past[s.past.length - 1]!, past: s.past.slice(0, -1) };
    }
    case 'clear':
      return Object.keys(s.lines).length === 0 ? s : { ...s, past: [...s.past, s.lines], lines: {} };
    case 'remove': {
      if (!(a.selection in s.lines)) return s;
      const next = { ...s.lines };
      delete next[a.selection];
      return { ...s, past: [...s.past, s.lines], lines: next };
    }
    case 'accepted': {
      const next = { ...s.lines };
      delete next[a.selection];
      return { ...s, past: [], lines: next };
    }
    case 'repeat':
      // Repeat only fills the draft; the player still has to confirm.
      return { ...s, past: [...s.past, s.lines], lines: { ...a.lines } };
    case 'set':
      return { ...s, past: [...s.past, s.lines], lines: { ...a.lines } };
  }
}

export function draftTotal(lines: DraftState['lines']): bigint {
  return Object.values(lines).reduce((acc, v) => acc + BigInt(v ?? '0'), 0n);
}

export function draftEntries(lines: DraftState['lines']): [GameSelection, bigint][] {
  return (Object.entries(lines) as [GameSelection, string][]).filter(([, v]) => BigInt(v) > 0n).map(([k, v]) => [k, BigInt(v)]);
}
